import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { OrderListReferenceRowError } from '@1po/1po-bff-fe-spec/generated/order_list/model/OrderListReferenceRowError';
import { List } from 'antd';
import { TFunction } from 'i18next';
import { NO_FILE } from 'components/FileUpload/paramsByState';
import {
  getOrderListFileUploadErrorRows,
  setOrderListFileModalStatus,
  setOrderListFileReferencesIsModalOpen,
} from 'domains/orderList/OrderList.store';
import { ErrorDetails } from 'pages/CartPage/CartStep/FileUpload/FileReferencesRowsErrors.styled';
import { ErrorAlert, BlackButton, Box, Flex, MarginBox, Text } from 'UI';
import { TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE } from 'utils/rights/tractionBatteryOrder';

type rowError = OrderListReferenceRowError['errorCode'];

const errorCodeTranslationKeys: Record<rowError, [string, string]> = {
  NO_REFERENCE: ['order_list.file_upload.row.no_reference', 'Row without reference'],
  UNKNOWN_REFERENCE: ['order_list.file_upload.row.unknown_reference', 'Row with unknown reference'],
  NOT_SOLD: ['order_list.file_upload.row.not_sold', 'Product no longer sold'],
  TECHNICAL_ERROR: ['order_list.file_upload.row.technical_error', 'Technical error'],
  INVALID_QUANTITY: ['order_list.file_upload.row.invalid_quantity', 'Invalid quantity'],
  QUANTITY_EXCEEDS_999: ['order_list.file_upload.row.quantity_exceeds_999', 'Quantity exceeds 999'],
  OUT_OF_STOCK: ['order_list.file_upload.row.out_of_stock', 'Product is out of stock'],
  PRICE_NOT_AVAILABLE: ['order_list.file_upload.row.price_not_available', 'Product price unavailable'],
  TRACTION_BATTERY_ORDER_NOT_AUTHORIZED: [
    'order_list.file_upload.row.traction_battery_order_not_authorized',
    'You are not allowed to order traction battery parts',
  ],
  TRACTION_BATTERY_VIN_REQUIRED: [
    'order_list.file_upload.row.traction_battery_vin_required',
    'Identify car with VIN to order traction battery part',
  ],
  TRACTION_BATTERY_VIN_NON_COMPLIANT: [
    'order_list.file_upload.row.traction_battery_vin_non_compliant',
    TRACTION_BATTERY_VIN_NON_COMPLIANT_DEFAULT_MESSAGE,
  ],
  TRACTION_BATTERY_ZE_READY_REQUIRED: [
    'order_list.file_upload.row.traction_battery_ze_ready_required',
    'ZE-Ready homologation is required to purchase this product',
  ],
};

interface EListI {
  data: OrderListReferenceRowError[];
}

function EList(props: EListI) {
  const { t } = useTranslation();
  return (
    <List
      dataSource={props.data}
      renderItem={(item) => (
        <List.Item>
          <Text type="base" bold>{`${t('common.row', 'Row')} ${item.rowNumber}`}</Text>
          <Box width={5} />
          <Text type="base" color="grey35">
            {' '}
            {translateErrorCode(item.errorCode, t)}
          </Text>
          <Flex />
        </List.Item>
      )}
      split={false}
    />
  );
}

function translateErrorCode(code: rowError, t: TFunction): string | null {
  const translation = errorCodeTranslationKeys[code];
  return translation ? t(translation[0], translation[1]) : null;
}

export default function OrderListReferencesRowsErrors() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const errorRows = useSelector(getOrderListFileUploadErrorRows);

  function handleTryAgain() {
    dispatch(setOrderListFileReferencesIsModalOpen(true));
    dispatch(setOrderListFileModalStatus(NO_FILE));
  }

  return (
    <Flex direction={'column'} minWidth={300} align={'center'}>
      <MarginBox mt={64}>
        <Box width={900}>
          <ErrorAlert
            message={
              <Flex direction={'row'} justify={'space-between'}>
                <Text type="base" color="grey35">
                  {t(
                    'common.file_upload.items_could_not_be_added',
                    'One or several items in your file need to be checked.',
                  )}
                </Text>
              </Flex>
            }
          />
          <ErrorDetails>
            <Box align={'left'}>
              <Text type={'text'} displayStyle={'error'}>
                {t('common.details', 'Details')}
              </Text>
              {errorRows.length > 0 && <EList data={errorRows} />}
            </Box>
            <Flex justify={'flex-end'}>
              <MarginBox ml={15} />
              <BlackButton stretch={false} size={'middle'} onClick={() => handleTryAgain()}>
                {t('common.try_again', 'Try again')}
              </BlackButton>
            </Flex>
          </ErrorDetails>
        </Box>
      </MarginBox>
    </Flex>
  );
}
